import { useState, useEffect, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const AUTO_ADVANCE_MS = 6000;

// Full-bleed, cinematic fade-slideshow for the Untamed gallery — deliberately
// not a snap/drag carousel (this is a mood/proof gallery, not a product
// picker). Auto-advances slowly, pauses on hover/focus, and is fully
// operable via the arrows/dots without waiting for the timer.
export const UntamedCarousel = ({ images }) => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const goTo = useCallback((i) => setActive((i + images.length) % images.length), [images.length]);
  const next = useCallback(() => goTo(active + 1), [active, goTo]);
  const prev = useCallback(() => goTo(active - 1), [active, goTo]);

  useEffect(() => {
    if (paused || images.length <= 1) return;
    timerRef.current = setTimeout(next, AUTO_ADVANCE_MS);
    return () => clearTimeout(timerRef.current);
  }, [active, paused, next, images.length]);

  return (
    <div
      data-testid="untamed-carousel"
      className="relative h-[60vh] md:h-[85vh] overflow-hidden bg-black"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {images.map((img, i) => (
        <div
          key={img.src}
          className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
          style={{ opacity: i === active ? 1 : 0, pointerEvents: i === active ? "auto" : "none" }}
        >
          {img.fit === "contain" && (
            <img
              src={img.src}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-50"
            />
          )}
          <img
            src={img.src}
            alt={img.alt}
            data-testid={`untamed-carousel-slide-${i}`}
            className={`absolute inset-0 w-full h-full ${img.fit === "contain" ? "object-contain" : "object-cover"}`}
            style={{ objectPosition: img.position || "center" }}
            loading={i === 0 ? "eager" : "lazy"}
            decoding="async"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20 pointer-events-none" />

      <button
        type="button"
        onClick={prev}
        data-testid="untamed-carousel-prev"
        aria-label="Previous photo"
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-11 h-11 bg-black/50 backdrop-blur-sm border border-white/15 text-white hover:bg-[var(--brand-primary)] hover:text-black hover:border-[var(--brand-primary)] transition-colors flex items-center justify-center"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        type="button"
        onClick={next}
        data-testid="untamed-carousel-next"
        aria-label="Next photo"
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-11 h-11 bg-black/50 backdrop-blur-sm border border-white/15 text-white hover:bg-[var(--brand-primary)] hover:text-black hover:border-[var(--brand-primary)] transition-colors flex items-center justify-center"
      >
        <ChevronRight size={20} />
      </button>

      <div className="absolute bottom-6 left-0 right-0 flex items-center justify-center gap-2.5">
        {images.map((img, i) => (
          <button
            type="button"
            key={img.src}
            onClick={() => goTo(i)}
            data-testid={`untamed-carousel-dot-${i}`}
            aria-label={`Go to photo ${i + 1}`}
            aria-current={i === active}
            className={`h-1.5 transition-all ${i === active ? "w-8 bg-[var(--brand-primary)]" : "w-1.5 bg-white/50 hover:bg-white/80"}`}
          />
        ))}
      </div>
    </div>
  );
};
